import React, { useState } from 'react';

import { styled } from '@uhg-abyss/web/tools/styled';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { BottomSheetDialog } from '../../../../components/BottomSheetDialog';
import { useRedirect, useContentConfig, useFeatureFlag } from '../hooks';

type ChatWelcomeBannerProps = Record<string, never>;

const ChatWelcomeBanner: React.FC<ChatWelcomeBannerProps> = () => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { handleClose } = useRedirect();
  const { data: config, loading } = useContentConfig();
  const isInfoButtonEnabled = useFeatureFlag('bannerInfoButton.enabled');
  const isInfoButtonVisible = useFeatureFlag('bannerInfoButton.visible');

  const bannerContent = config?.content?.banner;

  const openConfirm = () => setIsConfirmOpen(true);
  const closeConfirm = () => setIsConfirmOpen(false);

  const handleEndChat = () => {
    closeConfirm();
    handleClose();
  };

  if (loading) {
    return (
      <HeroContainer
        bg="$core.color.warm-white.5"
        borderBottomColor="$core.color.neutral.20"
        role="banner"
      >
        <RowContainer>
          <div style={{ padding: '10px', textAlign: 'center', flex: 1 }}>Loading...</div>
        </RowContainer>
      </HeroContainer>
    );
  }

  return (
    <HeroContainer
      bg="$core.color.warm-white.5"
      borderBottomColor="$core.color.neutral.20"
      role="banner"
    >
      <RowContainer>
        <IconButton
          type="button"
          aria-label={bannerContent?.closeAriaLabel}
          onPress={openConfirm}
          onClick={openConfirm}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.575 7.975L1.675 12.875C1.49167 13.0583 1.25833 13.15 0.975 13.15C0.691667 13.15 0.458333 13.0583 0.275 12.875C0.0916667 12.6917 0 12.4583 0 12.175C0 11.8917 0.0916667 11.6583 0.275 11.475L5.175 6.575L0.275 1.675C0.0916667 1.49167 0 1.25833 0 0.975C0 0.691667 0.0916667 0.458333 0.275 0.275C0.458333 0.0916667 0.691667 0 0.975 0C1.25833 0 1.49167 0.0916667 1.675 0.275L6.575 5.175L11.475 0.275C11.6583 0.0916667 11.8917 0 12.175 0C12.4583 0 12.6917 0.0916667 12.875 0.275C13.0583 0.458333 13.15 0.691667 13.15 0.975C13.15 1.25833 13.0583 1.49167 12.875 1.675L7.975 6.575L12.875 11.475C13.0583 11.6583 13.15 11.8917 13.15 12.175C13.15 12.4583 13.0583 12.6917 12.875 12.875C12.6917 13.0583 12.4583 13.15 12.175 13.15C11.8917 13.15 11.6583 13.0583 11.475 12.875L6.575 7.975Z"
              fill="#002677"
            />
          </svg>
        </IconButton>
        <HeaderText>{bannerContent?.title}</HeaderText>
        {isInfoButtonVisible ? (
          <IconButton
            type="button"
            aria-label={bannerContent?.infoAriaLabel}
            disabled={!isInfoButtonEnabled}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 15C10.2833 15 10.5208 14.9042 10.7125 14.7125C10.9042 14.5208 11 14.2833 11 14V10C11 9.71667 10.9042 9.47917 10.7125 9.2875C10.5208 9.09583 10.2833 9 10 9C9.71667 9 9.47917 9.09583 9.2875 9.2875C9.09583 9.47917 9 9.71667 9 10V14C9 14.2833 9.09583 14.5208 9.2875 14.7125C9.47917 14.9042 9.71667 15 10 15ZM10 7C10.2833 7 10.5208 6.90417 10.7125 6.7125C10.9042 6.52083 11 6.28333 11 6C11 5.71667 10.9042 5.47917 10.7125 5.2875C10.5208 5.09583 10.2833 5 10 5C9.71667 5 9.47917 5.09583 9.2875 5.2875C9.09583 5.47917 9 5.71667 9 6C9 6.28333 9.09583 6.52083 9.2875 6.7125C9.47917 6.90417 9.71667 7 10 7ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18Z"
                fill="#4B4D4F"
              />
            </svg>
          </IconButton>
        ) : (
          <div style={{ width: 44, height: 44 }} />
        )}
      </RowContainer>

      {isConfirmOpen ? (
        <BottomSheetDialog
          isOpen={isConfirmOpen}
          onClose={closeConfirm}
          ariaLabel="End chat confirmation"
          size="md"
          alertDialog
          hideClose
          closeOnClickOutside={false}
        >
          <Box color="">
            <Heading
              level={2}
              style={{
                fontFamily: 'Georgia, Times New Roman, serif',
                textAlign: 'center',
                fontSize: '1.25rem',
                fontWeight: 'bold',
              }}
            >
              {bannerContent?.endChatDialog.title}
            </Heading>
            <Box
              color=""
              style={{
                marginTop: '1rem',
                marginBottom: '1.5rem',
                textAlign: 'center',
              }}
            >
              <Text
                style={{
                  color: '#374151',
                  lineHeight: 1.5,
                  textAlign: 'center',
                  display: 'block',
                  width: '100%',
                }}
              >
                {bannerContent?.endChatDialog.message}
              </Text>
            </Box>

            <Box
              color=""
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1.25rem',
                width: '100%',
              }}
            >
              <Button
                variant="filled"
                color="brand"
                onClick={handleEndChat}
                aria-label={bannerContent?.endChatDialog.confirmAriaLabel}
                style={{
                  borderRadius: '9999px',
                  minHeight: '48px',
                  width: '100%',
                  fontWeight: 'bold',
                  backgroundColor: '#002677',
                  marginBottom: '12px',
                }}
              >
                {bannerContent?.endChatDialog.confirmButton}
              </Button>
              <Button
                variant="text"
                color="neutral"
                onClick={closeConfirm}
                aria-label={bannerContent?.endChatDialog.cancelAriaLabel}
                style={{
                  borderRadius: '9999px',
                  minHeight: '48px',
                  width: '100%',
                  fontWeight: 'bold',
                  marginTop: '4px',
                }}
              >
                {bannerContent?.endChatDialog.cancelButton}
              </Button>
            </Box>
          </Box>
        </BottomSheetDialog>
      ) : null}
    </HeroContainer>
  );
};

const HeroContainer = styled('div', {
  borderBottomWidth: '$core.border-width.sm',
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 1,
  boxShadow: '0 4px 6px -4px rgba(0, 0, 0, 0.15)',
});

const RowContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `$core.spacing.200 $core.spacing.300`,
});

const HeaderText = styled('div', {
  fontSize: 18,
  fontFamily: '$web.core.font-family.sf-pro-text-semi-bold',
});

const IconButton = styled('button', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: '$core.border-radius.round',
  padding: '$core.spacing.100',
  borderWidth: 0,
  backgroundColor: 'transparent',
  cursor: 'pointer',
});

export default ChatWelcomeBanner;
